import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { useParams, useNavigate } from 'react-router-dom';

import Nav from '../nav';
import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import WhiteLabelForm from '../../../components/whiteLabelForm';

import './ReportsPage.css';

window.html2canvas = html2canvas;

const PAGE_W = 816;
const PAGE_H = 1056;
const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const FieldCard = ({ label, value, primary }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 3,
      border: `1px solid ${alpha('#0f172a', 0.08)}`,
      background: alpha(primary, 0.04),
      minHeight: 84,
    }}
  >
    <Typography
      sx={{
        mb: 0.45,
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: '#64748b',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.98rem',
        fontWeight: 600,
        lineHeight: 1.45,
        color: '#0f172a',
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

const SignatureBlock = ({ label, name, title }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box
      sx={{
        height: 42,
        mb: 1,
        borderBottom: '1px solid #cbd5e1',
      }}
    />
    <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a' }}>
      {name}
    </Typography>
    <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.45 }}>
      {title}
    </Typography>
    <Typography sx={{ mt: 0.4, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

const AttestationLetterAPIPentest = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const [pentests, setPentests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [primaryColors, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client !== undefined) setClientName(client);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${BASEURL}/pentests`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') {
          navigate('/');
          return;
        }

        setPentests(data?.pentests || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const current = pentests.find((item) => item.id === Number(pentest_id));

  const today = useMemo(() => new Date(), []);
  const dateStr = useMemo(
    () => today.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    [today]
  );

  const BRAND = primaryColors || '#0A68FF';
  const BRAND_DARK = darken(BRAND, 0.2);
  const BRAND_DEEP = darken(BRAND, 0.38);
  const BRAND_LIGHT = lighten(BRAND, 0.28);
  const ORG = companyName || 'StealthNet AI';
  const certificateId = `${ORG.replace(/\s+/g, '').toUpperCase()}-API-${pentest_id}-${today.getFullYear()}`;
  const verificationUrl = `${window.location.origin}/verify/attestation/${pentest_id}?type=api`;
  const issuedTo = clientName || current?.client_name || current?.company || 'Client Organization';
  const engagementName = current?.name || `API Pentest #${pentest_id}`;

  const handleDownloadPDF = () => {
    html2pdf()
      .set({
        margin: 0,
        filename: `${ORG.replace(/\s+/g, '')}_API_Pentest_Certificate_${pentest_id}.pdf`,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: false },
        jsPDF: { unit: 'px', format: [PAGE_W, PAGE_H], orientation: 'portrait' },
      })
      .from(reportRef.current)
      .save();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box className="no-print" sx={{ position: 'relative', pb: 2 }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            background: DEFAULT_PRIMARY,
            '&:hover': { background: darken(DEFAULT_PRIMARY, 0.12) },
          }}
        >
          Download Certificate
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ minHeight: '100vh', py: 2, px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto', pb: 3 }}>
          <Box
            sx={{
              width: PAGE_W,
              minWidth: PAGE_W,
              borderRadius: '30px',
              overflow: 'hidden',
              boxShadow: '0 40px 90px rgba(2, 6, 23, 0.16)',
            }}
          >
            <Box
              ref={reportRef}
              sx={{
                width: PAGE_W,
                height: PAGE_H,
                position: 'relative',
                overflow: 'hidden',
                background: '#ffffff',
                color: '#0f172a',
                fontFamily: '"Poppins", "Inter", sans-serif',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    `radial-gradient(420px 260px at 88% 10%, ${alpha(BRAND_LIGHT, 0.18)} 0%, transparent 68%),
                     radial-gradient(520px 320px at 10% 100%, ${alpha(BRAND, 0.08)} 0%, transparent 72%),
                     linear-gradient(180deg, #fdfefe 0%, #f7fafe 100%)`,
                }}
              />

              <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    position: 'relative',
                    px: 7,
                    pt: 6.5,
                    pb: 5.5,
                    background: `linear-gradient(145deg, ${BRAND_DEEP} 0%, #0c1524 46%, #122035 100%)`,
                    color: '#ffffff',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: -36,
                      right: -34,
                      width: 220,
                      height: 220,
                      borderRadius: '30px',
                      background: `linear-gradient(180deg, ${alpha(BRAND_LIGHT, 0.2)} 0%, ${alpha(BRAND, 0.06)} 100%)`,
                      border: `1px solid ${alpha('#dbeafe', 0.12)}`,
                      transform: 'rotate(12deg)',
                    }}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4.25 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", "Poppins", sans-serif',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                        color: alpha('#dbeafe', 0.94),
                      }}
                    >
                      {ORG}
                    </Typography>

                    <Chip
                      label="ASSESSMENT ATTESTATION"
                      size="small"
                      sx={{
                        bgcolor: alpha('#ffffff', 0.1),
                        color: '#ffffff',
                        border: `1px solid ${alpha('#dbeafe', 0.16)}`,
                        borderRadius: '999px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                      }}
                    />
                  </Stack>

                  <Typography
                    sx={{
                      fontFamily: '"Inter", "Poppins", sans-serif',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: alpha('#cbd5e1', 0.98),
                      mb: 1.35,
                    }}
                  >
                    Attestation Letter
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: '"Inter", "Poppins", sans-serif',
                      fontSize: '2.55rem',
                      fontWeight: 800,
                      lineHeight: 1.06,
                      letterSpacing: '-0.035em',
                      maxWidth: 470,
                      mb: 2.1,
                    }}
                  >
                    Certificate of Attestation
                  </Typography>

                  <Box
                    sx={{
                      width: 170,
                      height: 4,
                      borderRadius: '999px',
                      bgcolor: BRAND,
                      mb: 2.2,
                    }}
                  />

                  <Typography
                    sx={{
                      maxWidth: 560,
                      fontSize: '1rem',
                      lineHeight: 1.7,
                      color: alpha('#dbeafe', 0.92),
                    }}
                  >
                    This letter confirms that the engagement below underwent an independent API penetration test and that formal reporting was delivered with validated findings, observed risk, and remediation guidance.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 7,
                    pb: 6,
                    mt: -3,
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 6,
                      border: `1px solid ${alpha('#0f172a', 0.08)}`,
                      background: '#ffffff',
                      boxShadow: '0 18px 44px rgba(15, 23, 42, 0.1)',
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1.5,
                      }}
                    >
                      <FieldCard label="Issued To" value={issuedTo} primary={BRAND} />
                      <FieldCard label="Engagement" value={engagementName} primary={BRAND} />
                      <FieldCard label="Assessment Type" value="API Penetration Test" primary={BRAND} />
                      <FieldCard label="Date of Issue" value={dateStr} primary={BRAND} />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 3.2,
                      borderRadius: 4,
                      border: `1px solid ${alpha(BRAND, 0.14)}`,
                      background: alpha(BRAND, 0.045),
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", "Poppins", sans-serif',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: '#0f172a',
                        mb: 1.2,
                      }}
                    >
                      Assessment Statement
                    </Typography>
                    <Typography sx={{ fontSize: '0.98rem', lineHeight: 1.7, color: '#334155' }}>
                      {ORG} attests that qualified security personnel performed an authorized review of the client&apos;s API environment using established endpoint mapping, authorization analysis, validation, and reporting practices. A formal deliverable was produced to document validated findings, evidence, and prioritized remediation guidance based on the conditions observed during the engagement window.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.45fr 0.95fr',
                      gap: 2.25,
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        border: `1px solid ${alpha('#0f172a', 0.08)}`,
                        background: '#ffffff',
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.4 }}>
                        <ShieldOutlinedIcon sx={{ color: BRAND_DARK }} />
                        <Typography
                          sx={{
                            fontFamily: '"Inter", "Poppins", sans-serif',
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: '#0f172a',
                          }}
                        >
                          Included With This Attestation
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.35 }}>
                        {[
                          { icon: <VerifiedOutlinedIcon fontSize="small" />, label: 'Validated findings' },
                          { icon: <DescriptionOutlinedIcon fontSize="small" />, label: 'Formal reporting delivered' },
                          { icon: <WorkspacePremiumOutlinedIcon fontSize="small" />, label: 'Executive-ready summary' },
                        ].map((item) => (
                          <Chip
                            key={item.label}
                            icon={item.icon}
                            label={item.label}
                            size="small"
                            sx={{
                              bgcolor: alpha(BRAND, 0.08),
                              color: BRAND_DARK,
                              border: `1px solid ${alpha(BRAND, 0.18)}`,
                              borderRadius: '999px',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>

                      <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.65, color: '#475569' }}>
                        This attestation reflects the completion of the authorized assessment and confirms that the client received a documented output suitable for executive review, stakeholder distribution, and internal remediation planning.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        border: `1px solid ${alpha('#0f172a', 0.08)}`,
                        background: '#ffffff',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box
                        sx={{
                          width: 92,
                          height: 92,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          mb: 1.6,
                          background: `radial-gradient(circle at 35% 35%, #ffffff 0%, ${alpha(BRAND_LIGHT, 0.8)} 42%, ${alpha(BRAND, 0.16)} 100%)`,
                          border: `2px solid ${alpha(BRAND, 0.24)}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 800,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: BRAND_DARK,
                            textAlign: 'center',
                          }}
                        >
                          Verified
                        </Typography>
                      </Box>

                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', mb: 0.45 }}>
                        Certificate ID
                      </Typography>
                      <Typography sx={{ fontSize: '0.94rem', fontWeight: 700, color: '#0f172a', mb: 1.2 }}>
                        {certificateId}
                      </Typography>

                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', mb: 0.45 }}>
                        Verification
                      </Typography>
                      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: BRAND_DARK }}>
                        <LaunchOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.5, color: '#475569', overflowWrap: 'anywhere' }}>
                          {verificationUrl}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 'auto', pt: 2.3 }}>
                    <Divider sx={{ borderColor: '#dbe3ef', mb: 2.2 }} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 3.5,
                      }}
                    >
                      <SignatureBlock label="Assessment Lead" name="Authorized Reviewer" title={`Lead Pentester | ${ORG}`} />
                      <SignatureBlock label="Quality Assurance" name="Independent Reviewer" title={`Quality Assurance | ${ORG}`} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AttestationLetterAPIPentest;
